// React context for CfpAuthUtils
import React, { createContext, useContext, useEffect, useState } from 'react';
import { CfpAuthUtils } from '../utils/cfpAuthUtils';
import { getUpdatedConfig } from '../config/env';
import logger from '../utils/logger';

interface CfpAuthContextType {
  cfpAuthUtils: CfpAuthUtils;
  loading: boolean;
  isRegistered: boolean;
  isAuthenticated: boolean;
}

const CfpAuthContext = createContext<CfpAuthContextType | undefined>(undefined);

// Create a singleton instance of CfpAuthUtils
const cfpAuthUtils = new CfpAuthUtils();

export const CfpAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [loading, setLoading] = useState(cfpAuthUtils.loading);
  const [isRegistered, setIsRegistered] = useState(cfpAuthUtils.isRegistered);
  const [isAuthenticated, setIsAuthenticated] = useState(cfpAuthUtils.isAuthenticated);

  useEffect(() => {
    // Make cfpAuthUtils globally available for redirect
    (window as any).cfpAuthUtils = cfpAuthUtils;
    
    // Skip initialization on redirect page to prevent duplicate calls
    if (window.location.pathname === '/redirect') {
      logger.info('🔄 Skipping CfpAuthContext initialization on redirect page');
      return;
    }
    
    const initializeAuth = async () => {
      // Initialize the CFP provider first
      await cfpAuthUtils.initialize(getUpdatedConfig());
      
      // Then check authentication status
      logger.info('✅ CFP provider initialized, checking auth status...');
      await cfpAuthUtils.checkAuthStatus();
    };
    
    initializeAuth();
    
    // Subscribe to auth state changes
    const unsubscribe = cfpAuthUtils.onStateChange((state: { loading: boolean; isRegistered: boolean; isAuthenticated: boolean }) => {
      setLoading(state.loading);
      setIsRegistered(state.isRegistered);
      setIsAuthenticated(state.isAuthenticated);
    });

    // Cleanup subscription on unmount
    return unsubscribe;
  }, []);

  const contextValue: CfpAuthContextType = {
    cfpAuthUtils,
    loading,
    isRegistered,
    isAuthenticated,
  };

  return (
    <CfpAuthContext.Provider value={contextValue}>
      {children}
    </CfpAuthContext.Provider>
  );
};

// Custom hook to use the cfp auth context
export const useCfpAuthUtilities = (): CfpAuthContextType => {
  const context = useContext(CfpAuthContext);
  if (context === undefined) {
    throw new Error('useCfpAuthUtilities must be used within a CfpAuthProvider');
  }
  return context;
};

export default CfpAuthContext;
